import { useState } from 'react';
import { useT } from '../../i18n';

export interface BookingPaymentModalProps {
  /** Amount to charge, in NOK; shown in the summary and passed back on success. */
  amount: number;
  /** Human-readable summary of what is being paid for (court/date/time). */
  bookingSummary: string;
  /** Closes the modal without charging. */
  onCancel: () => void;
  /** Called with the mock payment receipt once the (simulated) payment succeeds. */
  onSuccess: (receipt: {
    method: string;
    transactionId: string;
    paidAt: number;
    amount: number;
  }) => void;
}

/** Mock payment terminal (Vipps/card, simulated processing + 5% decline); extracted verbatim from the original artifact (legacy/App.jsx). */
export function BookingPaymentModal({
  amount,
  bookingSummary,
  onCancel,
  onSuccess,
}: BookingPaymentModalProps) {
  const t = useT();
  const [method, setMethod] = useState(null); // 'vipps' | 'card' | null
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const processFakePayment = async (chosenMethod) => {
    setMethod(chosenMethod);
    setProcessing(true);
    setError(null);
    // Simulated wait — in production this would be a POST to the payment API
    await new Promise((r) => setTimeout(r, 1500));
    // 5% chance of a simulated decline, so admins can test the failure flow
    if (Math.random() < 0.05) {
      setProcessing(false);
      setError(t('booking.payment.declined'));
      return;
    }
    onSuccess({
      method: chosenMethod,
      transactionId: `mock_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      paidAt: Date.now(),
      amount,
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 22, 17, 0.75)',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          maxWidth: 420,
          width: '100%',
          maxHeight: 'calc(100dvh - 100px)',
          marginTop: 80,
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
          border: '1px solid #e7eae3',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '14px 20px',
            borderBottom: '1px solid #e7eae3',
            background: '#fafbf7',
            flexShrink: 0,
          }}
        >
          <h3 className="font-display text-lg font-semibold leading-tight">
            {t('booking.payment.title')}
          </h3>
          <p className="text-[11px] text-ink-3 mt-0.5 italic">{t('booking.payment.mockNote')}</p>
        </div>
        <div style={{ padding: 20, overflowY: 'auto', flex: '1 1 auto' }} className="space-y-4">
          {/* Booking summary */}
          <div className="rounded-lg bg-bg-soft/40 border border-line p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-3 mb-1">
              {t('booking.payment.payingFor')}
            </div>
            <div className="text-sm font-semibold text-ink-1">{bookingSummary}</div>
            <div className="flex items-baseline justify-between mt-2 pt-2 border-t border-line">
              <span className="text-xs text-ink-2">{t('booking.payment.total')}</span>
              <span className="font-display text-2xl font-bold text-emerald-700">
                {t('booking.payment.amountKr', { amount: String(amount) })}
              </span>
            </div>
          </div>

          {processing ? (
            <div className="text-center py-8">
              <div className="inline-block w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mb-3" />
              <div className="text-sm font-semibold text-ink-1">
                {t('booking.payment.processing')}
              </div>
              <div className="text-[11px] text-ink-3 mt-1">
                {t(
                  method === 'vipps' ? 'booking.payment.checkVipps' : 'booking.payment.contactBank',
                )}
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="rounded-lg bg-rose-soft border border-rose/30 px-3 py-2 text-xs text-rose font-semibold">
                  {error}
                </div>
              )}
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-3">
                {t('booking.payment.chooseMethod')}
              </div>
              <button
                onClick={() => processFakePayment('vipps')}
                className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-line hover:border-orange-400 hover:bg-orange-50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-500 text-white font-bold flex items-center justify-center text-sm flex-shrink-0">
                  Vipps
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-ink-1">Vipps</div>
                  <div className="text-[11px] text-ink-3">{t('booking.payment.vippsHint')}</div>
                </div>
              </button>
              <button
                onClick={() => processFakePayment('card')}
                className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-line hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-600 text-white font-bold flex items-center justify-center text-xs flex-shrink-0">
                  {t('booking.payment.card')}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-ink-1">
                    {t('booking.payment.card')}
                  </div>
                  <div className="text-[11px] text-ink-3">Visa, Mastercard, Amex</div>
                </div>
              </button>
            </>
          )}
        </div>
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e7eae3',
            background: '#fafbf7',
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
          }}
        >
          <button
            onClick={onCancel}
            disabled={processing}
            className="btn-outline text-sm disabled:opacity-40"
          >
            {t(processing ? 'booking.payment.cancelling' : 'booking.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
